import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, AlertTriangle, Shield, FileWarning } from "lucide-react";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";
import { blogPosts } from "@/data/blogPosts";
import { Helmet } from "@/components/Helmet";

const Blog = () => {
  const posts = blogPosts;

  // Determine icon based on post content
  const getPostIcon = (title: string) => {
    if (title.toLowerCase().includes('vulnerability') || title.toLowerCase().includes('exploiting')) {
      return <AlertTriangle className="h-5 w-5 text-amber-400" />;
    }
    if (title.toLowerCase().includes('wrap-up') || title.toLowerCase().includes('weekly')) {
      return <FileWarning className="h-5 w-5 text-primary" />;
    }
    return <Shield className="h-5 w-5 text-green-400" />;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>ShadowScan Blog | WordPress Threat Intelligence</title>
        <meta
          name="description"
          content="Read ShadowScan's WordPress threat intelligence: vulnerability breakdowns, exploitation alerts, and practical guidance for reducing risk."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "@id": "https://shadowscan.com.au/blog/#blog",
            mainEntityOfPage: "https://shadowscan.com.au/blog",
            name: "ShadowScan Security Blog",
            description: "WordPress threat intelligence, vulnerability disclosures, and security insights from the ShadowScan team.",
            publisher: {
              "@type": "Organization",
              "@id": "https://shadowscan.com.au/#organization",
              name: "ShadowScan",
              logo: {
                "@type": "ImageObject",
                url: "https://shadowscan.com.au/og-image.png"
              }
            },
            blogPost: posts.map(post => ({
              "@type": "BlogPosting",
              headline: post.title,
              description: post.excerpt,
              datePublished: new Date(post.date).toISOString(),
              url: `https://shadowscan.com.au/blog/${post.slug}`,
              author: {
                "@type": "Organization",
                name: "ShadowScan Team"
              },
              publisher: {
                "@type": "Organization",
                name: "ShadowScan",
                logo: {
                  "@type": "ImageObject",
                  url: "https://shadowscan.com.au/og-image.png"
                }
              }
            }))
          })}
        </script>
      </Helmet>
      <Navigation />
      
      <main className="flex-grow pt-24">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// THREAT_INTELLIGENCE</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                Security <span className="text-gradient">Insights</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Continuous vulnerability disclosures, threat analysis, and WordPress security intelligence from the ShadowScan team.
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Newsletter Subscribe */}
              <div className="mb-12">
                <NewsletterSubscribe />
              </div>

              <div className="space-y-6">
                {posts.map((post, index) => (
                  <Card 
                    key={index} 
                    className="cyber-card border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg group overflow-hidden"
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {getPostIcon(post.title)}
                          <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                            <Calendar className="h-4 w-4" />
                            <span>{post.date}</span>
                          </div>
                        </div>
                        {index === 0 && (
                          <span className="px-2 py-1 text-xs font-mono font-medium bg-accent/20 text-accent border border-accent/30 rounded">
                            LATEST
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-xl md:text-2xl font-heading group-hover:text-primary transition-colors leading-tight">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </CardTitle>
                      <CardDescription className="text-base text-muted-foreground leading-relaxed mt-3">
                        {post.excerpt}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <Button 
                        variant="ghost" 
                        className="group/btn text-primary hover:text-primary hover:bg-primary/10 font-medium p-0" 
                        asChild
                      >
                        <Link to={`/blog/${post.slug}`}>
                          Read Analysis
                          <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Subscribe CTA */}
              <div className="text-center mt-16 py-12 border border-border rounded-xl bg-gradient-to-br from-primary/5 to-accent/5">
                <div className="font-mono text-sm text-primary mb-4">// STAY_INFORMED</div>
                <h3 className="text-xl font-heading font-bold mb-2">Get Security Alerts</h3>
                <p className="text-muted-foreground">
                  Subscribe to receive vulnerability disclosures and threat intelligence updates.
                </p>
              </div>

              {/* Contextual Internal Links */}
              <div className="mt-12 grid gap-4 md:grid-cols-2">
                <Link
                  to="/services"
                  className="block p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors"
                >
                  <h4 className="font-heading font-semibold text-foreground mb-1">Managed WordPress security services</h4>
                  <p className="text-sm text-muted-foreground">
                    See how we harden sites, apply application-level protections, and monitor signals continuously.
                  </p>
                </Link>
                <Link
                  to="/contact"
                  className="block p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors"
                >
                  <h4 className="font-heading font-semibold text-foreground mb-1">Request a free, limited external scan</h4>
                  <p className="text-sm text-muted-foreground">
                    Get a no-cost assessment with prioritised findings for your WordPress site.
                  </p>
                </Link>
                <Link
                  to="/about"
                  className="block p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors"
                >
                  <h4 className="font-heading font-semibold text-foreground mb-1">Meet the ShadowScan security team</h4>
                  <p className="text-sm text-muted-foreground">
                    Learn about the analysts and engineers behind our threat intelligence.
                  </p>
                </Link>
                <Link
                  to="/portal/login"
                  className="block p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors"
                >
                  <h4 className="font-heading font-semibold text-foreground mb-1">Access the customer portal</h4>
                  <p className="text-sm text-muted-foreground">
                    Manage protected domains, view alerts, and update billing in one place.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
